// web/src/screens/privacy.ts — Phase 5 D-05 / PRIV-05 / BETA-01.
//
// /privacy screen: parental retention toggle + GDPR copy.
// Reachable from the picker config button (via /config) and from the
// parental consent screen.
//
// Body copy is fetched from /storage/legal/privacy_lv.txt (served by
// internal/httpx/web_embed.go MountLegal). The toggle binds to
// GET / PUT /api/config/privacy with silent revert on PUT failure
// (UI-SPEC §"Error state for privacy toggle failure").
import { navigate } from "../main.js";
import { getPrivacy, setPrivacy } from "../api/privacy.js";

export function mount(root: HTMLElement, _props?: unknown): () => void {
  // Do NOT clear body theme class — preserve any active game theme so the
  // back-button returns the user to a styled picker.

  root.innerHTML = `
    <section class="screen-privacy" style="
      min-height: 100dvh;
      padding: var(--space-lg);
      background: var(--surface);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-lg);
    ">
      <div style="width: 100%; max-width: 520px;">
        <button class="back-link" style="
          background: none; border: 0;
          font-size: var(--type-body);
          color: var(--text-muted);
          padding: var(--space-sm) var(--space-md);
          min-height: 44px;
          cursor: pointer;
        " aria-label="Atpakaļ">← Atpakaļ</button>
      </div>
      <h1 style="
        font-size: var(--type-heading);
        font-weight: 600;
        text-align: center;
        max-width: 520px;
        margin: 0;
      ">Privātums un audio glabāšana</h1>
      <pre class="privacy-body" style="
        font-size: var(--type-body);
        line-height: 1.5;
        color: var(--text-primary);
        max-width: 520px;
        white-space: pre-wrap;
        font-family: var(--font-body);
        margin: 0;
      ">Ielādē...</pre>
      <label class="toggle-row" style="
        display: flex; align-items: center; gap: var(--space-md);
        max-width: 520px; min-height: 64px;
        font-size: var(--type-body); color: var(--text-primary);
        cursor: pointer;
      ">
        <input type="checkbox" class="retention-toggle" disabled style="
          width: 28px; height: 28px; cursor: pointer;
        " />
        <span>Saglabāt audio kvalitātes pārbaudei (max 30 dienas)</span>
      </label>
      <p style="
        font-size: var(--type-body);
        color: var(--text-muted);
        max-width: 520px;
        margin: 0;
      ">Mēs izmantojam to tikai lai uzlabotu spēli. Audio netiek koplietots.</p>
      <p style="
        font-size: var(--type-body);
        color: var(--text-primary);
        max-width: 520px;
        margin: 0;
      ">Datu jautājumiem: <a href="mailto:janis.viksne@gmail.com" style="color: var(--text-primary);">janis.viksne@gmail.com</a></p>
    </section>
  `;

  const back = root.querySelector(".back-link") as HTMLButtonElement;
  const toggle = root.querySelector(".retention-toggle") as HTMLInputElement;
  const bodyEl = root.querySelector(".privacy-body") as HTMLElement;

  function onBack(): void { history.back(); }

  // Load privacy_lv.txt body. textContent assignment is XSS-safe — never
  // innerHTML — even though the source is operator-controlled (T-05-03-01).
  fetch("/storage/legal/privacy_lv.txt", { credentials: "include" })
    .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
    .then((text) => { bodyEl.textContent = text; })
    .catch(() => { bodyEl.textContent = ""; });

  // Load current toggle state. If unauthenticated (RequireSession 401),
  // redirect to login per UI-SPEC.
  getPrivacy()
    .then((p) => {
      toggle.checked = p.retention_enabled;
      toggle.disabled = false;
    })
    .catch(() => {
      navigate("/login");
    });

  async function onToggle(): Promise<void> {
    const desired = toggle.checked;
    toggle.disabled = true;
    try {
      await setPrivacy(desired);
      toggle.disabled = false;
    } catch {
      // Silent revert per UI-SPEC §"Error state for privacy toggle failure"
      toggle.checked = !desired;
      toggle.disabled = false;
    }
  }

  back.addEventListener("click", onBack);
  toggle.addEventListener("change", onToggle);

  return (): void => {
    back.removeEventListener("click", onBack);
    toggle.removeEventListener("change", onToggle);
  };
}
